import { useCallback, useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Check,
  ChevronDown,
  Copy,
  Key,
  KeyRound,
  Link2,
  MessagesSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  ShieldAlert,
  Trash2,
} from "lucide-react";

import {
  ConnectionRow,
  type ConnectionProviderIcon,
  type ConnectionRowAction,
  type NormalizedConnectionStatus,
} from "@/components/connection-row";
import {
  GoogleIcon,
  getApiKeyProviderIcon,
  McpIcon,
  Microsoft365Icon,
  SlackIcon,
} from "@/components/provider-icons";
import { SlackInstallSection } from "@/components/slack-install-section";
import { ByokKeysPanel } from "@/components/byok-keys-panel";
import { McpServersPanel } from "@/components/mcp-servers-panel";
import { SkillList, formatSkillName } from "@/components/skill-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAutoResize } from "@/hooks/use-auto-resize";
import { Spinner } from "@/components/ui/spinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  type ConnectionsData,
  type Ms365Connection,
  type Ms365ConnectionLevel,
  getMs365OrganizationConnections,
  supportsMs365ConnectionLevels,
  resolveMs365ConnectionsForUser,
  useConnections,
} from "@/hooks/use-connections";
import {
  type GoogleConnection,
  type GoogleConnectionLevel,
  type GoogleConnectionsData,
  type GoogleService,
  useGoogleConnections,
} from "@/hooks/use-google-connections";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { useMcpAgentAccess } from "@/hooks/use-mcp-agent-access";
import {
  type AvailableSecret,
  type StoredSecret,
  useUserSecrets,
} from "@/hooks/use-user-secrets";
import {
  type SkillWiringSummary,
  useSkillWiring,
} from "@/hooks/use-skill-wiring";
import {
  type ComposioCompatibilityProbeResult,
  probeComposioSkillConnections,
} from "@/hooks/use-composio-skill-connection";
import { getTenantAgentEditorAccess, type Me } from "@/hooks/use-me";

export { normalizedSkillWiringStatus } from "@/components/skill-list";

const MS365_SCOPE_OPTIONS = [
  { scope: "Notes.Read", labelKey: "connections:ms365.scope.oneNoteRead" },
  { scope: "Notes.ReadWrite", labelKey: "connections:ms365.scope.oneNoteReadWrite" },
  { scope: "Mail.Read", labelKey: "connections:ms365.scope.mailRead" },
  { scope: "Mail.ReadWrite", labelKey: "connections:ms365.scope.mailReadWrite" },
  { scope: "Mail.Send", labelKey: "connections:ms365.scope.mailSend" },
] as const;

const DEFAULT_ONBOARDING_SCOPE_SET = ["Notes.Read", "Mail.Read"] as const;

function isEntraTenantNotResolvedError(error: unknown): boolean {
  return (
    typeof error === "object"
    && error !== null
    && "code" in error
    && error.code === "entra_tenant_not_resolved"
  );
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

type ConnectionMechanism = "oauth" | "api_key" | "mcp";

interface ProviderIdentity {
  id: string;
  icon: ConnectionProviderIcon;
}

interface ProviderDefinition<TSource> extends ProviderIdentity {
  mechanism: ConnectionMechanism;
  getStatus: (source: TSource) => NormalizedConnectionStatus;
}

interface Microsoft365ProviderSource {
  connections: Ms365Connection[];
  error: string | null;
  identityState: "loading" | "resolved" | "unresolved";
}

interface Microsoft365ProviderDefinition
  extends ProviderDefinition<Microsoft365ProviderSource> {
  id: "microsoft-365";
  mechanism: "oauth";
}

interface GoogleProviderSource {
  data: GoogleConnectionsData | null;
  error: string | null;
}

interface GoogleProviderDefinition extends ProviderDefinition<GoogleProviderSource> {
  id: "google";
  mechanism: "oauth";
}

interface ApiKeyProviderSource {
  storedSecret: StoredSecret | undefined;
  error: string | null;
}

interface ApiKeyProviderDefinition extends ProviderDefinition<ApiKeyProviderSource> {
  mechanism: "api_key";
  secret: AvailableSecret;
  displayName: string;
  description: string;
}

function microsoft365Status({
  connections,
  error,
  identityState,
}: Microsoft365ProviderSource): NormalizedConnectionStatus {
  if (error) return "error";
  if (identityState === "loading") return "loading";
  if (identityState === "unresolved") return "error";

  if (connections.length === 0) return "not_connected";

  return connections.every((connection) => connection.status === "active")
    ? "connected"
    : "needs_attention";
}

function googleStatus({ data, error }: GoogleProviderSource): NormalizedConnectionStatus {
  if (error) return "error";
  const connections = [
    ...(data?.personal_connections ?? []),
    ...(data?.organization_connections ?? []),
  ];
  if (connections.length === 0) return "not_connected";
  return connections.every((connection) => connection.status === "active")
    ? "connected"
    : "needs_attention";
}

function apiKeyStatus({ storedSecret, error }: ApiKeyProviderSource): NormalizedConnectionStatus {
  if (error) return "error";
  if (!storedSecret) return "not_connected";

  const status = storedSecret.status.toLowerCase();
  if (["invalid", "expired", "revoked", "disabled", "error", "needs_attention"].includes(status)) {
    return "needs_attention";
  }

  return "connected";
}

/**
 * Static OAuth providers live here. Adding another OAuth integration only
 * requires another provider definition plus its adapter content below.
 */
const OAUTH_PROVIDER_REGISTRY: Array<Microsoft365ProviderDefinition | GoogleProviderDefinition> = [
  {
    id: "microsoft-365",
    icon: Microsoft365Icon,
    mechanism: "oauth",
    getStatus: microsoft365Status,
  },
  {
    id: "google",
    icon: GoogleIcon,
    mechanism: "oauth",
    getStatus: googleStatus,
  },
];

function secretKey(secret: AvailableSecret): string {
  return secret.path + "/" + secret.name;
}

function isByokManagedSecret(secret: AvailableSecret): boolean {
  const path = secret.path.trim().replace(/^\/+|\/+$/g, "").toLowerCase();
  // The available-secret contract normally exposes BYOK as path "byok" and
  // provider name "openai" (which resolves to byok/openai). Accept a nested
  // path as well so these dedicated keys never fall back into generic rows.
  return path === "byok" || path.startsWith("byok/");
}

/**
 * Generic API-key providers are declared from the available-secret contract.
 * BYOK keys use their dedicated, tier-aware panel instead, while all other
 * generic providers get the same row and token-management behavior
 * automatically.
 */
function createApiKeyProvider(secret: AvailableSecret, t: TFunction): ApiKeyProviderDefinition {
  const providerKey = secret.path.toLowerCase();
  const icon = getApiKeyProviderIcon(providerKey) ?? Key;

  return {
    id: "api-key:" + secretKey(secret),
    displayName: secret.description,
    icon,
    description: t("connections:provider.genericDescription", { service: secret.description }),
    mechanism: "api_key",
    secret,
    getStatus: apiKeyStatus,
  };
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function scopesSummary(t: TFunction, scopes: string[]): string {
  const hasSend = scopes.includes("Mail.Send");
  const hasWrite = scopes.includes("Notes.ReadWrite") || scopes.includes("Mail.ReadWrite");

  if (hasSend) return t("connections:ms365.readWriteAndSend");
  if (hasWrite) return t("connections:ms365.readWrite");
  if (scopes.length > 0) return t("connections:status.readOnly");
  return t("connections:ms365.noScopesReported");
}

function microsoft365StatusDescription(
  t: TFunction,
  status: NormalizedConnectionStatus,
  connections: Ms365Connection[],
  identityState: Microsoft365ProviderSource["identityState"],
): string {
  switch (status) {
    case "connected":
      if (connections.length === 1) {
        return connections[0].user_email
          ? t("connections:ms365.connectedAs", { email: connections[0].user_email })
          : t("connections:ms365.oneAccountConnected");
      }
      return t("connections:ms365.accountsConnected", { count: connections.length });
    case "needs_attention":
      return t("connections:ms365.reconnectToRestoreAccess");
    case "error":
      return identityState === "unresolved"
        ? t("connections:ms365.couldNotVerifyIdentity")
        : t("connections:ms365.couldNotLoadStatus");
    case "loading":
      return t("connections:ms365.checkingStatus");
    default:
      return t("connections:ms365.noAccountConnected");
  }
}

function apiKeyStatusDescription(t: TFunction, status: NormalizedConnectionStatus): string {
  switch (status) {
    case "connected":
      return t("connections:apiKey.tokenSaved");
    case "needs_attention":
      return t("connections:apiKey.updateTokenToRestoreAccess");
    case "error":
      return t("connections:apiKey.couldNotLoadStatus");
    default:
      return t("connections:apiKey.noTokenSaved");
  }
}

interface Microsoft365ConnectionProviderProps {
  provider: Microsoft365ProviderDefinition;
  data: ConnectionsData | null;
  loading: boolean;
  error: string | null;
  me: Me | null;
  meLoading: boolean;
  refresh: () => Promise<void>;
  connect: (scopes?: string[], userEmail?: string, level?: Ms365ConnectionLevel) => Promise<string | null>;
  disconnect: (userOid: string) => Promise<boolean>;
  generateOnboardingLink: (
    entraTenantId?: string,
    customerName?: string,
    scopes?: string[],
  ) => Promise<string | null>;
}

function Microsoft365ConnectionProvider({
  provider,
  data,
  loading,
  error,
  me,
  meLoading,
  refresh,
  connect,
  disconnect,
  generateOnboardingLink,
}: Microsoft365ConnectionProviderProps) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard(2000);
  const [connecting, setConnecting] = useState(false);
  const [connectLevelOpen, setConnectLevelOpen] = useState(false);
  const [connectLevel, setConnectLevel] = useState<Ms365ConnectionLevel>("user");
  const [pendingAuth, setPendingAuth] = useState<{ level: Ms365ConnectionLevel; scopes?: string[] }>({ level: "user" });
  const [disconnecting, setDisconnecting] = useState(false);
  const [upgradingScopes, setUpgradingScopes] = useState(false);
  const [entraTenantId, setEntraTenantId] = useState("");
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [onboardingLink, setOnboardingLink] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [microsoftEmailDialogOpen, setMicrosoftEmailDialogOpen] = useState(false);
  const [microsoftEmail, setMicrosoftEmail] = useState("");
  const [microsoftEmailError, setMicrosoftEmailError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  // Feature scopes are opt-in: an empty selection means no scopes parameter is
  // sent, so the server grants only the identity base (openid, profile,
  // User.Read, offline_access).
  const [selectedScopeSet, setSelectedScopeSet] = useState<Set<string>>(
    () => new Set<string>(),
  );
  // Shareable links have their own selection, fixed into the link by the
  // authenticated admin before the public onboarding flow begins.
  const [onboardingScopeSet, setOnboardingScopeSet] = useState<Set<string>>(
    () => new Set<string>(DEFAULT_ONBOARDING_SCOPE_SET),
  );

  const supportsLevels = supportsMs365ConnectionLevels(data);
  const organization = getMs365OrganizationConnections(data);
  const canManageOrganization = supportsLevels && data?.can_manage_organization === true;
  const hasOrganization = organization.some((connection) => connection.status !== "revoked");
  const resolution = resolveMs365ConnectionsForUser(data?.connections, me);
  const connections = resolution.connections;
  const identityState: Microsoft365ProviderSource["identityState"] =
    meLoading || (data === null && error === null)
      ? "loading"
      : resolution.matchState === "unresolved"
        ? "unresolved"
        : "resolved";
  const visibleConnections = [...connections, ...organization.filter((connection) => connection.status !== "revoked")];
  const status = provider.getStatus({ connections: visibleConnections, error, identityState });
  const knownTenantId = connections.find((connection) => connection.entra_tenant_id)?.entra_tenant_id;
  const selectedScopes = useMemo(
    () => MS365_SCOPE_OPTIONS
      .filter(({ scope }) => selectedScopeSet.has(scope))
      .map(({ scope }) => scope),
    [selectedScopeSet],
  );
  const requestedScopes = selectedScopes.length > 0 ? selectedScopes : undefined;
  const onboardingScopes = useMemo(
    () => MS365_SCOPE_OPTIONS
      .filter(({ scope }) => onboardingScopeSet.has(scope))
      .map(({ scope }) => scope),
    [onboardingScopeSet],
  );

  const toggleScope = useCallback((scope: string) => {
    setSelectedScopeSet((current) => {
      const next = new Set(current);
      if (next.has(scope)) {
        next.delete(scope);
      } else {
        next.add(scope);
      }
      return next;
    });
    setConnectError(null);
  }, []);

  const toggleOnboardingScope = useCallback((scope: string) => {
    setOnboardingScopeSet((current) => {
      const next = new Set(current);
      if (next.has(scope)) {
        next.delete(scope);
      } else {
        next.add(scope);
      }
      return next;
    });
    setOnboardingLink(null);
    setLinkError(null);
  }, []);

  const startAuthentication = useCallback(
    async (level: Ms365ConnectionLevel = "user", account?: Ms365Connection) => {
      setConnectError(null);
      const scopes = requestedScopes ?? account?.scopes;
      setPendingAuth({ level, scopes });
      try {
        const url = level === "organization"
          ? await connect(scopes, account?.user_email ?? undefined, level)
          : await connect(scopes);
        if (url) {
          window.location.href = url;
        } else {
          setConnectError(t("connections:ms365.failedToGenerateSignInUrl"));
          setScopePickerOpen(true);
        }
      } catch (err) {
        if (isEntraTenantNotResolvedError(err)) {
          setMicrosoftEmailError(null);
          setMicrosoftEmailDialogOpen(true);
        } else {
          setConnectError(errorMessage(err, t("connections:ms365.failedToGenerateSignInUrl")));
          setScopePickerOpen(true);
        }
      }
    },
    [connect, requestedScopes, t],
  );

  const handleMicrosoftEmailSubmit = useCallback(async () => {
    const enteredEmail = microsoftEmail.trim();
    if (!enteredEmail) return;

    setMicrosoftEmailError(null);
    setConnecting(true);
    try {
      const url = pendingAuth.level === "organization"
        ? await connect(pendingAuth.scopes, enteredEmail, "organization")
        : await connect(requestedScopes, enteredEmail);
      if (url) {
        window.location.href = url;
      } else {
        setMicrosoftEmailError(t("connections:ms365.failedToGenerateSignInUrl"));
        setConnectError(t("connections:ms365.failedToGenerateSignInUrl"));
        setScopePickerOpen(true);
      }
    } catch (err) {
      if (isEntraTenantNotResolvedError(err)) {
        setMicrosoftEmailError(
          t("connections:ms365.tenantNotFound"),
        );
      } else {
        const message = errorMessage(err, t("connections:ms365.failedToGenerateSignInUrl"));
        setMicrosoftEmailError(message);
        setConnectError(message);
        setScopePickerOpen(true);
      }
    } finally {
      setConnecting(false);
    }
  }, [connect, microsoftEmail, pendingAuth, requestedScopes, t]);

  const handleConnect = useCallback(() => {
    if (canManageOrganization) {
      setConnectLevel("user");
      setConnectLevelOpen(true);
      return;
    }
    setConnecting(true);
    void startAuthentication().finally(() => setConnecting(false));
  }, [canManageOrganization, startAuthentication]);

  const handleSharedReconnect = (account: Ms365Connection) => {
    setConnecting(true);
    void startAuthentication("organization", account).finally(() => setConnecting(false));
  };

  const handleSharedDisconnect = async (account: Ms365Connection) => {
    setDisconnecting(true);
    try {
      await disconnect(account.user_oid);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleUpgradeScopes = useCallback(() => {
    setUpgradingScopes(true);
    void startAuthentication().finally(() => setUpgradingScopes(false));
  }, [startAuthentication]);

  const handleOpenOnboardingLinkDialog = useCallback(() => {
    setLinkError(null);
    setOnboardingLink(null);
    setLinkDialogOpen(true);
  }, []);

  const handleGenerateLink = useCallback(() => {
    setLinkError(null);

    if (!knownTenantId && !entraTenantId.trim()) {
      setOnboardingLink(null);
      setLinkDialogOpen(true);
      return;
    }

    setGeneratingLink(true);
    void generateOnboardingLink(
      knownTenantId || entraTenantId.trim() || undefined,
      undefined,
      onboardingScopes,
    )
      .then((url) => {
        if (url) {
          setOnboardingLink(url);
        } else {
          setLinkError(t("connections:ms365.failedToGenerateOnboardingLink"));
        }
        setLinkDialogOpen(true);
      })
      .catch(() => {
        setLinkError(t("connections:ms365.failedToGenerateOnboardingLink"));
        setLinkDialogOpen(true);
      })
      .finally(() => setGeneratingLink(false));
  }, [entraTenantId, generateOnboardingLink, knownTenantId, onboardingScopes, t]);

  const reconnectRequired = status === "needs_attention"
    || (status === "error" && identityState === "unresolved");
  const canStartConnection = (supportsLevels && connections.length === 0)
    || status === "not_connected"
    || reconnectRequired
    || (status === "error" && identityState === "resolved" && connections.length === 0);
  const connectionActionLabel = reconnectRequired
    ? t("connections:actions.reconnect")
    : t("connections:actions.connect");

  const primaryAction: ConnectionRowAction = (() => {
    if (status === "connected") {
      return {
        label: t("connections:actions.manage"),
        opensDetails: true,
        disabled: loading,
      };
    }

    if (status === "needs_attention") {
      return {
        label: t("connections:actions.reconnect"),
        icon: <RefreshCw />,
        opensDetails: true,
        disabled: loading || connecting,
        loading: connecting,
      };
    }

    if (status === "error") {
      return {
        label: reconnectRequired
          ? t("connections:actions.reconnect")
          : t("connections:actions.manage"),
        icon: reconnectRequired ? <RefreshCw /> : <ShieldAlert />,
        opensDetails: true,
        disabled: loading || connecting,
        loading: reconnectRequired && connecting,
      };
    }

    if (status === "loading") {
      return {
        label: t("connections:actions.connect"),
        icon: <Plus />,
        opensDetails: true,
        disabled: true,
      };
    }

    return {
      label: t("connections:actions.connect"),
      icon: <Plus />,
      opensDetails: true,
      disabled: loading || connecting,
      loading: connecting,
    };
  })();

  return (
    <>
      <ConnectionRow
        providerId={provider.id}
        icon={provider.icon}
        name={t("connections:provider.microsoft365")}
        description={t("connections:provider.microsoft365Description")}
        status={status}
        statusDescription={microsoft365StatusDescription(t, status, visibleConnections, identityState)}
        action={primaryAction}
        loading={loading || identityState === "loading"}
        details={
          <div className="space-y-4">
            {error && (
              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                role="alert"
              >
                <span className="flex items-center gap-2">
                  <AlertCircle className="size-4" />
                  {error}
                </span>
                <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
                  <RefreshCw />
                  {t("common:actions.retry")}
                </Button>
              </div>
            )}

            <Collapsible open={scopePickerOpen} onOpenChange={setScopePickerOpen}>
              <CollapsibleTrigger className="border px-3 py-2 hover:bg-muted/50">
                <span className="text-left">
                  <span className="block">{t("connections:ms365.chooseAccess")}</span>
                  <span className="block text-xs font-normal text-muted-foreground">
                    {t("connections:ms365.chooseAccessDescription")}
                  </span>
                </span>
                <ChevronDown className="chevron size-4 text-muted-foreground" />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-3 pb-3">
                <div className="grid gap-2 pt-3 sm:grid-cols-2">
                  {MS365_SCOPE_OPTIONS.map((option) => (
                    <label
                      key={option.scope}
                      className="flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={selectedScopeSet.has(option.scope)}
                        onChange={() => toggleScope(option.scope)}
                      />
                      <span>{t(option.labelKey)}</span>
                    </label>
                  ))}
                </div>
                {connectError && (
                  <p className="mt-3 text-sm text-destructive" role="alert">{connectError}</p>
                )}
              </CollapsibleContent>
            </Collapsible>

            {supportsLevels && <h4 className="font-medium">{t("connections:ms365.justMe")}</h4>}
            {connections.length > 0 ? (
              <div className="space-y-3">
                {connections.map((connection) => (
                  <div key={connection.id} className="rounded-md border bg-background p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-foreground">
                          {connection.user_email || connection.user_oid}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {connection.status === "active"
                            ? t("connections:ms365.credentialsActive")
                            : t("connections:ms365.accountMayNeedReconnect")}
                        </p>
                      </div>
                      <Badge variant="outline">{scopesSummary(t, connection.scopes)}</Badge>
                    </div>

                    <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="text-muted-foreground">{t("connections:status.connected")}</dt>
                        <dd className="mt-1 font-medium text-foreground">
                          {formatDate(connection.created_at)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">{t("connections:ms365.lastSync")}</dt>
                        <dd className="mt-1 font-medium text-foreground">
                          {formatDateTime(connection.last_sync_at)}
                        </dd>
                      </div>
                    </dl>

                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleUpgradeScopes}
                        disabled={upgradingScopes}
                      >
                        <RefreshCw className={upgradingScopes ? "animate-spin" : undefined} />
                        {upgradingScopes
                          ? t("connections:actions.upgrading")
                          : t("connections:actions.upgradeScopes")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("connections:ms365.noAccountDetails")}
              </p>
            )}

            {canStartConnection && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleConnect}
                  disabled={loading || connecting}
                  aria-label={`${connectionActionLabel} ${t("connections:provider.microsoft365")}`}
                >
                  {connecting
                    ? <RefreshCw className="animate-spin" />
                    : reconnectRequired ? <RefreshCw /> : <Plus />}
                  {connectionActionLabel}
                </Button>
              </div>
            )}

            {supportsLevels && (
              <section className="space-y-3 border-t pt-4" aria-label={t("connections:ms365.thisOrganization")}>
                <div>
                  <h4 className="font-medium">{t("connections:ms365.thisOrganization")}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{t("connections:ms365.sharedDescription")}</p>
                </div>
                {organization.map((account) => (
                  <div key={account.id} className="space-y-3 rounded-md border bg-background p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{account.label && account.label !== "Shared Microsoft account" ? account.label : t("connections:ms365.sharedAccount")}</p>
                        <p className="text-sm text-muted-foreground">{account.user_email || account.user_oid}</p>
                      </div>
                      <Badge variant="secondary">{t("connections:ms365.shared")}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {account.status === "revoked" ? t("connections:ms365.disconnected")
                        : account.status === "active" ? t("connections:ms365.credentialsActive")
                        : t("connections:ms365.accountMayNeedReconnect")}
                    </p>
                    <Badge variant="outline">{scopesSummary(t, account.scopes)}</Badge>
                    {canManageOrganization && (
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleSharedReconnect(account)} disabled={connecting || disconnecting}>
                          <RefreshCw />{t("connections:actions.reconnect")}
                        </Button>
                        {account.status !== "revoked" && (
                          <Button variant="outline" size="sm" onClick={() => void handleSharedDisconnect(account)} disabled={connecting || disconnecting}>
                            <Trash2 />{t("connections:actions.disconnect")}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {!hasOrganization && canManageOrganization && (
                  <Button variant="outline" size="sm" onClick={() => { setConnectLevel("organization"); setConnectLevelOpen(true); }} disabled={connecting || loading}>
                    <Plus />{t("connections:ms365.connectShared")}
                  </Button>
                )}
              </section>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed bg-background p-3">
              <div>
                <p className="font-medium text-foreground">{t("connections:ms365.itProviderOnboarding")}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("connections:ms365.itProviderOnboardingDescription")}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenOnboardingLinkDialog}
                disabled={generatingLink}
              >
                <Link2 />
                {generatingLink
                  ? t("connections:actions.generating")
                  : t("connections:actions.generateOnboardingLink")}
              </Button>
            </div>
          </div>
        }
      />

      {supportsLevels && canManageOrganization && (
        <Dialog open={connectLevelOpen} onOpenChange={setConnectLevelOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("connections:ms365.connectFor")}</DialogTitle>
              <DialogDescription>{t("connections:ms365.dedicatedAccountHint")}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-2 sm:grid-cols-2">
              <Button variant={connectLevel === "user" ? "default" : "outline"} onClick={() => setConnectLevel("user")} aria-pressed={connectLevel === "user"}>
                {t("connections:ms365.justMe")}
              </Button>
              <Button variant={connectLevel === "organization" ? "default" : "outline"} onClick={() => setConnectLevel("organization")} aria-pressed={connectLevel === "organization"} disabled={hasOrganization}>
                {t("connections:ms365.thisOrganization")}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => {
                setConnectLevelOpen(false);
                setConnecting(true);
                void startAuthentication(connectLevel).finally(() => setConnecting(false));
              }} disabled={connecting}>
                {t("connections:actions.continue")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("connections:ms365.onboardingLink")}</DialogTitle>
            <DialogDescription>
              {t("connections:ms365.onboardingLinkDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {linkError && <p className="text-sm text-destructive">{linkError}</p>}
            {!knownTenantId && (
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="link-tenant-id">
                  {t("connections:ms365.tenantDomainOrId")}
                </label>
                <Input
                  id="link-tenant-id"
                  placeholder={t("connections:ms365.tenantPlaceholder")}
                  value={entraTenantId}
                  onChange={(event) => setEntraTenantId(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t("connections:ms365.tenantHint")}
                </p>
              </div>
            )}
            {!onboardingLink && (
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-medium">
                    {t("connections:ms365.onboardingChooseAccess")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("connections:ms365.onboardingChooseAccessDescription")}
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {MS365_SCOPE_OPTIONS.map((option) => (
                    <label
                      key={option.scope}
                      className="flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={onboardingScopeSet.has(option.scope)}
                        onChange={() => toggleOnboardingScope(option.scope)}
                      />
                      <span>{t(option.labelKey)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {onboardingLink && (
              <div className="flex items-center gap-2 rounded-md border bg-muted p-2">
                <code className="flex-1 break-all text-xs">{onboardingLink}</code>
                <Button variant="ghost" size="icon" onClick={() => void copy(onboardingLink)}>
                  {copied ? <Check className="text-green-600 dark:text-green-400" /> : <Copy />}
                  <span className="sr-only">{t("connections:ms365.copyOnboardingLink")}</span>
                </Button>
              </div>
            )}
            <DialogFooter>
              {!onboardingLink && (
                <Button
                  onClick={handleGenerateLink}
                  disabled={generatingLink || (!knownTenantId && !entraTenantId.trim())}
                >
                  {generatingLink
                    ? t("connections:actions.generating")
                    : t("connections:actions.generateLink")}
                </Button>
              )}
              <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
                {t("connections:actions.done")}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={microsoftEmailDialogOpen}
        onOpenChange={(open) => {
          setMicrosoftEmailDialogOpen(open);
          if (!open) setMicrosoftEmailError(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("connections:ms365.workSchoolEmailTitle")}</DialogTitle>
            <DialogDescription>
              {t("connections:ms365.workSchoolEmailDescription")}
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void handleMicrosoftEmailSubmit();
            }}
          >
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="microsoft-365-email">
                {t("connections:ms365.workSchoolEmail")}
              </label>
              <Input
                id="microsoft-365-email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                placeholder={t("connections:ms365.emailPlaceholder")}
                value={microsoftEmail}
                onChange={(event) => {
                  setMicrosoftEmail(event.target.value);
                  setMicrosoftEmailError(null);
                }}
              />
            </div>
            {microsoftEmailError && <p className="text-sm text-destructive">{microsoftEmailError}</p>}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMicrosoftEmailDialogOpen(false)}
                disabled={connecting}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button type="submit" disabled={connecting || !microsoftEmail.trim()}>
                {connecting ? t("connections:ms365.connecting") : t("connections:actions.continue")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const GOOGLE_SCOPE_OPTIONS = [
  {
    scope: "https://www.googleapis.com/auth/drive.readonly",
    service: "drive" as const,
    labelKey: "connections:google.scope.driveRead",
    descriptionKey: "connections:google.scope.driveReadDescription",
  },
  {
    scope: "https://www.googleapis.com/auth/drive",
    service: "drive" as const,
    labelKey: "connections:google.scope.driveFullWrite",
    descriptionKey: "connections:google.scope.driveFullWriteDescription",
  },
  {
    scope: "https://www.googleapis.com/auth/gmail.readonly",
    service: "gmail" as const,
    labelKey: "connections:google.scope.gmailRead",
    descriptionKey: "connections:google.scope.gmailReadDescription",
  },
  {
    scope: "https://www.googleapis.com/auth/gmail.modify",
    service: "gmail" as const,
    labelKey: "connections:google.scope.gmailModify",
    descriptionKey: "connections:google.scope.gmailModifyDescription",
  },
  {
    scope: "https://www.googleapis.com/auth/gmail.compose",
    service: "gmail" as const,
    labelKey: "connections:google.scope.gmailCompose",
    descriptionKey: "connections:google.scope.gmailComposeDescription",
  },
  {
    scope: "https://www.googleapis.com/auth/gmail.send",
    service: "gmail" as const,
    labelKey: "connections:google.scope.gmailSend",
    descriptionKey: "connections:google.scope.gmailSendDescription",
  },
] as const;

function googleScopeLabel(t: TFunction, scope: string): string {
  const option = GOOGLE_SCOPE_OPTIONS.find((candidate) => candidate.scope === scope);
  return option ? t(option.labelKey) : scope;
}

function googleScopeCoveredByGrant(grantedScopes: string[], requestedScope: string): boolean {
  if (grantedScopes.includes(requestedScope)) return true;
  if (
    requestedScope === "https://www.googleapis.com/auth/drive.readonly" &&
    grantedScopes.includes("https://www.googleapis.com/auth/drive")
  ) return true;
  if (
    [
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://www.googleapis.com/auth/gmail.compose",
      "https://www.googleapis.com/auth/gmail.send",
    ].includes(requestedScope) &&
    grantedScopes.includes("https://www.googleapis.com/auth/gmail.modify")
  ) return true;
  return false;
}

function googleHealthLabel(t: TFunction, status: GoogleConnection["status"]): string {
  switch (status) {
    case "active": return t("connections:google.active");
    case "reauth_required": return t("connections:google.actionNeeded");
    case "consent_missing": return t("connections:google.consentMissing");
    case "revoked": return t("connections:google.disconnected");
    default: return t("connections:google.actionNeeded");
  }
}

function googleHealthBadgeClass(status: GoogleConnection["status"]): string {
  return status === "active"
    ? "border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/50 dark:text-green-300"
    : status === "revoked"
      ? "border-muted-foreground/30 text-muted-foreground"
      : "border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200";
}

function googleStatusDescription(
  t: TFunction,
  status: NormalizedConnectionStatus,
  data: GoogleConnectionsData | null,
): string {
  if (status === "error") return t("connections:google.couldNotLoadConnections");
  const personal = data?.personal_connections.length ?? 0;
  const organization = data?.organization_connections.length ?? 0;
  if (personal + organization === 0) return t("connections:google.noAccountsConnected");
  const parts: string[] = [];
  if (personal) parts.push(t("connections:google.personalCount", { count: personal }));
  if (organization) parts.push(t("connections:google.organizationCount", { count: organization }));
  return parts.join(" · ");
}

interface GoogleAccountCardProps {
  connection: GoogleConnection;
  siblingRevocation: boolean;
  onManagePermissions: (connection: GoogleConnection) => void;
  onSetDefault: (connection: GoogleConnection, service: GoogleService) => void;
  onRename: (connection: GoogleConnection) => void;
  onReconnect: (connection: GoogleConnection) => void;
  onDisconnect: (connection: GoogleConnection) => void;
  busy: boolean;
}

function GoogleAccountCard({
  connection,
  siblingRevocation,
  onManagePermissions,
  onSetDefault,
  onRename,
  onReconnect,
  onDisconnect,
  busy,
}: GoogleAccountCardProps) {
  const { t } = useTranslation();
  const displayName = connection.account_label || connection.google_email || t("connections:google.account");
  const scopes = connection.allowed_scopes.filter((scope) =>
    GOOGLE_SCOPE_OPTIONS.some((option) => option.scope === scope),
  );
  const supports = (service: GoogleService) => connection.services.includes(service);

  return (
    <div className="space-y-3 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-foreground">{displayName}</p>
          {connection.google_email && connection.account_label && (
            <p className="mt-0.5 text-sm text-muted-foreground">{connection.google_email}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge variant="outline">
              {connection.connection_level === "user"
                ? t("connections:google.personal")
                : t("connections:google.organization")}
            </Badge>
            <Badge variant="outline" className={googleHealthBadgeClass(connection.status)}>
              {googleHealthLabel(t, connection.status)}
            </Badge>
            {connection.default_for?.map((service) => (
              <Badge key={service} variant="secondary">
                {t("connections:google.defaultFor", {
                  service: service === "drive" ? t("connections:google.drive") : t("connections:google.gmail"),
                })}
              </Badge>
            ))}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("connections:google.manageAccount", { name: displayName })}
              disabled={busy}
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onManagePermissions(connection)}>
              {t("connections:actions.managePermissions")}
            </DropdownMenuItem>
            {supports("drive") && (
              <DropdownMenuItem onSelect={() => onSetDefault(connection, "drive")}>
                {t("connections:actions.setDefaultForDrive")}
              </DropdownMenuItem>
            )}
            {supports("gmail") && (
              <DropdownMenuItem onSelect={() => onSetDefault(connection, "gmail")}>
                {t("connections:actions.setDefaultForGmail")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => onRename(connection)}>
              {t("connections:actions.rename")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onReconnect(connection)}>
              {t("connections:actions.reconnect")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={() => onDisconnect(connection)}
            >
              {t("connections:actions.disconnect")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div
        className="flex flex-wrap gap-1.5"
        aria-label={t("connections:google.enabledPermissions")}
      >
        {scopes.length > 0 ? scopes.map((scope) => (
          <Badge key={scope} variant="outline">{googleScopeLabel(t, scope)}</Badge>
        )) : <span className="text-xs text-muted-foreground">{t("connections:google.noServicesEnabled")}</span>}
      </div>

      {connection.connection_level === "organization" && (
        <p className="text-xs text-muted-foreground">
          {t("connections:google.connectedBy", {
            user: connection.created_by_workos_user_id,
            date: formatDate(connection.created_at),
          })}
        </p>
      )}

      {siblingRevocation && (
        <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <ShieldAlert className="mt-0.5 size-4 shrink-0" />
          <span>
            {t("connections:google.siblingWarning")}
          </span>
        </div>
      )}
    </div>
  );
}

interface GoogleConnectionProviderProps {
  provider: GoogleProviderDefinition;
  data: GoogleConnectionsData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  connect: (level: GoogleConnectionLevel, scopes: string[], targetConnectionId?: string) => Promise<string | null>;
  updateConnection: (id: string, values: { account_label?: string | null; allowed_scopes?: string[] }) => Promise<GoogleConnection | null>;
  setDefault: (service: GoogleService, connectionId: string) => Promise<boolean>;
  disconnect: (id: string) => Promise<boolean>;
  reconnect: (id: string, scopes?: string[]) => Promise<string | null>;
}

function GoogleConnectionProvider({
  provider,
  data,
  loading,
  error,
  refresh,
  connect,
  updateConnection,
  setDefault,
  disconnect,
  reconnect,
}: GoogleConnectionProviderProps) {
  const { t } = useTranslation();
  const [connectOpen, setConnectOpen] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [level, setLevel] = useState<GoogleConnectionLevel | null>(null);
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
  const [permissionConnection, setPermissionConnection] = useState<GoogleConnection | null>(null);
  const [renameConnection, setRenameConnection] = useState<GoogleConnection | null>(null);
  const [label, setLabel] = useState("");
  const [busyConnectionId, setBusyConnectionId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const personal = data?.personal_connections ?? [];
  const organization = data?.organization_connections ?? [];
  const allConnections = useMemo(() => [...personal, ...organization], [personal, organization]);
  const status = provider.getStatus({ data, error });

  const startConnect = useCallback(() => {
    setLevel(null);
    setSelectedScopes(new Set());
    setConnectOpen(true);
  }, []);

  const redirectToGoogle = useCallback(async (
    connectionLevel: GoogleConnectionLevel,
    scopes: string[],
    targetConnectionId?: string,
  ) => {
    const url = await connect(connectionLevel, scopes, targetConnectionId);
    if (url) window.location.href = url;
  }, [connect]);

  const handleInitialConnect = useCallback(() => {
    if (!level || selectedScopes.size === 0) return;
    setStarting(true);
    void redirectToGoogle(level, [...selectedScopes]).finally(() => setStarting(false));
  }, [level, redirectToGoogle, selectedScopes]);

  const openPermissions = useCallback((connection: GoogleConnection) => {
    setPermissionConnection(connection);
    setSelectedScopes(new Set(connection.allowed_scopes.filter((scope) =>
      GOOGLE_SCOPE_OPTIONS.some((option) => option.scope === scope),
    )));
    setPermissionsOpen(true);
  }, []);

  const savePermissions = useCallback(() => {
    if (!permissionConnection) return;
    const scopes = [...selectedScopes];
    const needsConsent = scopes.some((scope) => !googleScopeCoveredByGrant(permissionConnection.granted_scopes, scope));
    setStarting(true);
    if (needsConsent) {
      void redirectToGoogle(permissionConnection.connection_level, scopes, permissionConnection.id)
        .finally(() => setStarting(false));
      return;
    }
    void updateConnection(permissionConnection.id, { allowed_scopes: scopes })
      .then((updated) => {
        if (updated) setPermissionsOpen(false);
      })
      .finally(() => setStarting(false));
  }, [permissionConnection, redirectToGoogle, selectedScopes, updateConnection]);

  const toggleScope = useCallback((scope: string) => {
    setSelectedScopes((current) => {
      const next = new Set(current);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }, []);

  const handleSetDefault = useCallback((connection: GoogleConnection, service: GoogleService) => {
    setBusyConnectionId(connection.id);
    void setDefault(service, connection.id).finally(() => setBusyConnectionId(null));
  }, [setDefault]);

  const openRename = useCallback((connection: GoogleConnection) => {
    setRenameConnection(connection);
    setLabel(connection.account_label || "");
    setRenameOpen(true);
  }, []);

  const saveRename = useCallback(() => {
    if (!renameConnection) return;
    setBusyConnectionId(renameConnection.id);
    void updateConnection(renameConnection.id, { account_label: label.trim() || null })
      .then((updated) => {
        if (updated) setRenameOpen(false);
      })
      .finally(() => setBusyConnectionId(null));
  }, [label, renameConnection, updateConnection]);

  const handleReconnect = useCallback((connection: GoogleConnection) => {
    setBusyConnectionId(connection.id);
    void reconnect(connection.id).then((url) => {
      if (url) window.location.href = url;
    }).finally(() => setBusyConnectionId(null));
  }, [reconnect]);

  const handleDisconnect = useCallback((connection: GoogleConnection) => {
    setBusyConnectionId(connection.id);
    void disconnect(connection.id).finally(() => setBusyConnectionId(null));
  }, [disconnect]);

  const revocationAffected = useCallback((connection: GoogleConnection) => {
    const siblings = allConnections.filter((candidate) => candidate.google_sub === connection.google_sub);
    return siblings.length > 1 && siblings.some((candidate) =>
      candidate.status === "reauth_required" || candidate.status === "consent_missing",
    );
  }, [allConnections]);

  const primaryAction: ConnectionRowAction = status === "error"
    ? {
        label: t("connections:actions.manage"),
        icon: <ShieldAlert />,
        opensDetails: true,
        disabled: loading || starting,
      }
    : status === "needs_attention"
      ? {
          label: t("connections:actions.manage"),
          icon: <ShieldAlert />,
          opensDetails: true,
          disabled: loading || starting,
        }
      : status === "connected"
        ? { label: t("connections:actions.manage"), opensDetails: true, disabled: loading || starting }
        : {
            label: t("connections:actions.connect"),
            icon: <Plus />,
            opensDetails: true,
            disabled: loading || starting,
          };

  const scopeState = (option: typeof GOOGLE_SCOPE_OPTIONS[number]) => {
    if (!permissionConnection) {
      return selectedScopes.has(option.scope)
        ? t("connections:google.selected")
        : t("connections:google.optional");
    }
    if (selectedScopes.has(option.scope)) return t("connections:google.enabled");
    return googleScopeCoveredByGrant(permissionConnection.granted_scopes, option.scope)
      ? t("connections:google.disabledLocally")
      : t("connections:google.availableToRequest");
  };

  const accountSection = (
    title: string,
    description: string,
    connections: GoogleConnection[],
    sectionLevel: GoogleConnectionLevel,
  ) => (
    <section className="space-y-3">
      <div>
        <h4 className="font-medium text-foreground">{title}</h4>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
      {connections.length > 0 ? (
        <div className="space-y-3">
          {connections.map((connection) => (
            <GoogleAccountCard
              key={connection.id}
              connection={connection}
              siblingRevocation={revocationAffected(connection)}
              onManagePermissions={openPermissions}
              onSetDefault={handleSetDefault}
              onRename={openRename}
              onReconnect={handleReconnect}
              onDisconnect={handleDisconnect}
              busy={busyConnectionId === connection.id}
            />
          ))}
        </div>
      ) : (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {sectionLevel === "user"
            ? t("connections:google.noPersonalAccountsConnected")
            : t("connections:google.noOrganizationAccountsConnected")}
        </p>
      )}
      <Button variant="outline" size="sm" onClick={startConnect} disabled={loading || starting}>
        <Plus />
        {t("connections:actions.connect")}
      </Button>
    </section>
  );

  return (
    <>
      <ConnectionRow
        providerId={provider.id}
        icon={provider.icon}
        name={t("connections:provider.google")}
        description={t("connections:provider.googleDescription")}
        status={status}
        statusDescription={googleStatusDescription(t, status, data)}
        action={primaryAction}
        loading={loading}
        details={
          <div className="space-y-6">
            {error && (
              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                role="alert"
              >
                <span className="flex items-center gap-2">
                  <AlertCircle className="size-4" />
                  {error}
                </span>
                <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
                  <RefreshCw />
                  {t("common:actions.retry")}
                </Button>
              </div>
            )}
            {accountSection(
              t("connections:google.personalConnections"),
              t("connections:google.personalConnectionsDescription"),
              personal,
              "user",
            )}
            {accountSection(
              t("connections:google.organizationConnections"),
              t("connections:google.organizationConnectionsDescription"),
              organization,
              "organization",
            )}
          </div>
        }
      />

      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("connections:google.connectAccount")}</DialogTitle>
            <DialogDescription>
              {t("connections:google.connectAccountDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{t("connections:google.availability")}</legend>
              <label className="flex cursor-pointer gap-3 rounded-md border p-3">
                <input type="radio" name="google-level" checked={level === "user"} onChange={() => setLevel("user")} />
                <span><span className="font-medium">{t("connections:google.justMe")}</span><span className="mt-1 block text-sm text-muted-foreground">{t("connections:google.justMeDescription")}</span></span>
              </label>
              <label className="flex cursor-pointer gap-3 rounded-md border p-3">
                <input type="radio" name="google-level" checked={level === "organization"} onChange={() => setLevel("organization")} />
                <span><span className="font-medium">{t("connections:google.thisOrganization")}</span><span className="mt-1 block text-sm text-muted-foreground">{t("connections:google.thisOrganizationDescription")}</span></span>
              </label>
            </fieldset>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{t("connections:google.choosePermissions")}</legend>
              {GOOGLE_SCOPE_OPTIONS.map((option) => (
                <label key={option.scope} className="flex cursor-pointer items-start justify-between gap-3 rounded-md border p-3">
                  <span className="flex gap-3"><input type="checkbox" checked={selectedScopes.has(option.scope)} onChange={() => toggleScope(option.scope)} /><span><span className="font-medium">{t(option.labelKey)}</span><span className="mt-1 block text-xs text-muted-foreground">{t(option.descriptionKey)}</span></span></span>
                  <Badge variant="outline">{option.service === "drive" ? t("connections:google.drive") : t("connections:google.gmail")}</Badge>
                </label>
              ))}
              <p className="text-xs text-muted-foreground">{t("connections:google.sendApprovalGated")}</p>
            </fieldset>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>{t("common:actions.cancel")}</Button>
            <Button onClick={handleInitialConnect} disabled={!level || selectedScopes.size === 0 || starting}>
              {starting ? <RefreshCw className="animate-spin" /> : null}
              {t("connections:actions.continueToGoogle")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={permissionsOpen} onOpenChange={setPermissionsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("connections:actions.managePermissions")}</DialogTitle>
            <DialogDescription>
              {t("connections:google.managePermissionsDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {GOOGLE_SCOPE_OPTIONS.map((option) => (
              <label key={option.scope} className="flex cursor-pointer items-start justify-between gap-3 rounded-md border p-3">
                <span className="flex gap-3"><input type="checkbox" checked={selectedScopes.has(option.scope)} onChange={() => toggleScope(option.scope)} /><span><span className="font-medium">{t(option.labelKey)}</span><span className="mt-1 block text-xs text-muted-foreground">{t(option.descriptionKey)}</span></span></span>
                <Badge variant="outline">{scopeState(option)}</Badge>
              </label>
            ))}
            <p className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <ShieldAlert className="size-4 shrink-0" />
              {t("connections:google.reducedPermissionWarning")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsOpen(false)}>{t("common:actions.cancel")}</Button>
            <Button onClick={savePermissions} disabled={starting}>
              {starting ? <RefreshCw className="animate-spin" /> : null}
              {t("connections:actions.savePermissions")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t("connections:google.renameConnection")}</DialogTitle><DialogDescription>{t("connections:google.renameDescription")}</DialogDescription></DialogHeader>
          <Input value={label} onChange={(event) => setLabel(event.target.value)} placeholder={t("connections:google.renamePlaceholder")} aria-label={t("connections:google.connectionLabel")} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>{t("common:actions.cancel")}</Button>
            <Button onClick={saveRename} disabled={!renameConnection || busyConnectionId === renameConnection?.id}><Pencil /> {t("common:actions.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ApiKeyConnectionProviderProps {
  provider: ApiKeyProviderDefinition;
  storedSecret: StoredSecret | undefined;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  saveSecret: (path: string, name: string, value: string) => Promise<boolean>;
  deleteSecret: (path: string, name: string) => Promise<boolean>;
}

function ApiKeyConnectionProvider({
  provider,
  storedSecret,
  loading,
  error,
  refresh,
  saveSecret,
  deleteSecret,
}: ApiKeyConnectionProviderProps) {
  const { t } = useTranslation();
  const [token, setToken] = useState("");
  const [updatingToken, setUpdatingToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const { ref: resizeRef, resizeForValueChange } = useAutoResize(10, 1);
  const status = provider.getStatus({ storedSecret, error });
  const connected = status === "connected";
  const tokenFormVisible = !connected || updatingToken;

  const handleSave = useCallback(() => {
    const value = token.trim();
    if (!value) return;

    setSaving(true);
    void saveSecret(provider.secret.path, provider.secret.name, value)
      .then((saved) => {
        if (saved) {
          setToken("");
          setUpdatingToken(false);
        }
      })
      .finally(() => setSaving(false));
  }, [provider.secret.name, provider.secret.path, saveSecret, token]);

  const handleDisconnect = useCallback(() => {
    setDisconnecting(true);
    void deleteSecret(provider.secret.path, provider.secret.name).finally(() => setDisconnecting(false));
  }, [deleteSecret, provider.secret.name, provider.secret.path]);

  const primaryAction: ConnectionRowAction = (() => {
    if (status === "connected") {
      return { label: t("connections:actions.manage"), opensDetails: true };
    }

    if (status === "needs_attention") {
      return { label: t("connections:actions.reconnect"), opensDetails: true, disabled: loading };
    }

    if (status === "error") {
      return {
        label: t("common:actions.retry"),
        onClick: () => void refresh(),
        icon: <RefreshCw />,
        disabled: loading,
      };
    }

    return {
      label: t("connections:actions.connect"),
      icon: <Plus />,
      opensDetails: true,
      disabled: loading,
    };
  })();

  return (
    <ConnectionRow
      providerId={provider.id}
      icon={provider.icon}
      name={provider.displayName}
      description={provider.description}
      status={status}
      statusDescription={apiKeyStatusDescription(t, status)}
      action={primaryAction}
      loading={loading}
      details={
        <div className="space-y-4">
          {error && (
            <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
              <AlertCircle className="size-4" />
              {error}
            </p>
          )}

          {connected && !updatingToken && (
            <>
              <p className="text-sm text-muted-foreground">
                {t("connections:apiKey.savedDescription")}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setUpdatingToken(true)}>
                  {t("connections:actions.updateApiToken")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                >
                  <Trash2 />
                  {disconnecting
                    ? t("connections:actions.disconnecting")
                    : t("connections:actions.disconnect")}
                </Button>
              </div>
            </>
          )}

          {tokenFormVisible && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {status === "needs_attention"
                  ? t("connections:apiKey.replaceTokenToRestoreAccess")
                  : connected
                    ? t("connections:apiKey.enterReplacementToken")
                    : t("connections:apiKey.enterTokenToConnect")}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Textarea
                  ref={resizeRef}
                  rows={1}
                  className="min-h-0"
                  autoComplete="off"
                  aria-label={t("connections:apiKey.tokenAriaLabel", { service: provider.displayName })}
                  placeholder={t("connections:apiKey.tokenPlaceholder")}
                  value={token}
                  onChange={(event) => {
                    setToken(event.target.value);
                    resizeForValueChange();
                  }}
                />
                <Button size="sm" onClick={handleSave} disabled={!token.trim() || saving}>
                  {saving ? <RefreshCw className="animate-spin" /> : <Key />}
                  {saving ? t("connections:actions.saving") : t("connections:actions.saveToken")}
                </Button>
              </div>
              {connected && (
                <Button variant="ghost" size="sm" onClick={() => setUpdatingToken(false)} disabled={saving}>
                  {t("common:actions.cancel")}
                </Button>
              )}
            </div>
          )}
        </div>
      }
    />
  );
}

function McpAgentAccessConnectionProvider({ getToken }: { getToken: () => Promise<string> }) {
  const { t } = useTranslation();
  const { data, loading, saving, error, unsupported, refresh, save, reset } = useMcpAgentAccess(getToken);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => { if (data) setSelected(new Set(data.selected_agent_ids)); }, [data]);
  const toggle = useCallback((agentId: string) => setSelected((current) => {
    const next = new Set(current); if (next.has(agentId)) next.delete(agentId); else next.add(agentId); return next;
  }), []);
  const detail = unsupported ? <p className="text-sm text-muted-foreground">{t("connections:mcpAgentAccess.serverUpgradeRequired")}</p>
    : loading && !data ? <div className="flex justify-center py-8"><Spinner className="size-6" /></div>
    : <div className="space-y-4">
        {!data?.profile_exists && <p className="text-sm text-muted-foreground">{t("connections:mcpAgentAccess.defaultAccess")}</p>}
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <fieldset className="space-y-2" disabled={saving || !data}>
          <legend className="sr-only">{t("connections:mcpAgentAccess.availableAgents")}</legend>
          {data?.available.map((agent) => <label key={agent.agent_id} className="flex cursor-pointer gap-3 rounded-md border p-3">
            <input type="checkbox" className="mt-1 size-4 accent-primary" checked={selected.has(agent.agent_id)} onChange={() => toggle(agent.agent_id)} aria-label={t("connections:mcpAgentAccess.allowAgent", { agent: agent.display_name })} />
            <span className="min-w-0 flex-1" title={agent.description ?? undefined}><span className="block text-sm font-medium">{agent.display_name}</span>{agent.description && <span className="block truncate text-xs text-muted-foreground">{agent.description}</span>}</span>
          </label>)}
        </fieldset>
        {data?.available.length === 0 && <p className="text-sm text-muted-foreground">{t("connections:mcpAgentAccess.noAgents")}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => { if (window.confirm(t("connections:mcpAgentAccess.resetConfirm"))) void reset(); }} disabled={saving || !data?.profile_exists}>{t("connections:mcpAgentAccess.reset")}</Button>
          <Button size="sm" onClick={() => void save([...selected].sort())} disabled={saving || !data}>{saving ? t("connections:actions.saving") : t("common:actions.save")}</Button>
        </div>
      </div>;
  return <ConnectionRow providerId="mcp-agent-access" icon={McpIcon} name={t("connections:provider.mcpAgentAccess")} description={t("connections:provider.mcpAgentAccessDescription")} status={unsupported ? "not_connected" : error ? "error" : loading ? "loading" : "connected"} statusDescription={unsupported ? t("connections:mcpAgentAccess.serverUpgradeRequired") : data?.profile_exists ? t("connections:mcpAgentAccess.customized") : t("connections:mcpAgentAccess.defaultAccess")} action={{ label: t("connections:actions.manage"), opensDetails: true }} loading={loading} details={detail} />;
}

interface ConnectionsListProps {
  getToken: () => Promise<string>;
}

const EMPTY_SKILL_WIRING: SkillWiringSummary[] = [];

type ComposioCallbackResult = "connected" | "failed" | "cancelled";

interface ComposioCallbackLocation {
  result: ComposioCallbackResult | null;
  requestedSkillId: string | null;
  hasComposioParams: boolean;
}

interface ComposioCallbackTarget {
  skillId: string;
  displayName: string;
}

function composioCallbackFromLocation(): ComposioCallbackLocation {
  if (typeof window === "undefined") {
    return { result: null, requestedSkillId: null, hasComposioParams: false };
  }
  const params = new URLSearchParams(window.location.search);
  const value = params.get("composio");
  return {
    result: value === "connected" || value === "failed" || value === "cancelled" ? value : null,
    // This remains an untrusted, value-free catalog lookup candidate only. It
    // is never shown or used to select an endpoint before descriptor matching.
    requestedSkillId: params.get("composio_skill"),
    hasComposioParams: params.has("composio") || params.has("composio_skill"),
  };
}

interface ComposioCompatibilityProbeState {
  enabled: boolean;
  loading: boolean;
  results: Record<string, ComposioCompatibilityProbeResult>;
}

/**
 * Compatibility-only N+1 detection for the full Connections screen. A new
 * catalog serializes `connection` for every entry (including explicit null),
 * so no probes run once that schema is present.
 */
function useComposioCompatibilityProbes(
  skills: SkillWiringSummary[],
  getToken: ConnectionsListProps["getToken"],
): ComposioCompatibilityProbeState {
  const hasExplicitConnectionSchema = skills.some(
    (summary) => summary.connectionSchemaPresent
      || summary.connection !== undefined
      || summary.connectionInvalid,
  );
  const skillIds = useMemo(() => skills.map((summary) => summary.skill_id), [skills]);
  const skillIdsKey = skillIds.join("\u0000");
  const enabled = skills.length > 0 && !hasExplicitConnectionSchema;
  const [state, setState] = useState<Omit<ComposioCompatibilityProbeState, "enabled">>({
    loading: false,
    results: {},
  });

  useEffect(() => {
    if (!enabled) {
      setState((current) => current.loading || Object.keys(current.results).length > 0
        ? { loading: false, results: {} }
        : current);
      return;
    }

    let cancelled = false;
    // The synchronous render before this effect sees `enabled` with no result
    // and is read-only; this state change only adds the checking indicator.
    setState({ loading: true, results: {} });
    void probeComposioSkillConnections(skillIds, getToken).then((results) => {
      if (!cancelled) setState({ loading: false, results });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, getToken, skillIds, skillIdsKey]);

  return { enabled, ...state };
}

function SkillWiringPanel({
  getToken,
  callback,
  openComposioSkillId = null,
  onCallbackTargetResolved,
}: ConnectionsListProps & {
  callback?: ComposioCallbackLocation | null;
  openComposioSkillId?: string | null;
  onCallbackTargetResolved?: (target: ComposioCallbackTarget | null) => void;
}) {
  const { t } = useTranslation();
  const skillWiring = useSkillWiring(getToken);
  const skills = skillWiring.data?.skills ?? EMPTY_SKILL_WIRING;
  const compatibility = useComposioCompatibilityProbes(skills, getToken);
  const trustedCallbackTarget = useMemo<ComposioCallbackTarget | null>(() => {
    if (!callback?.result || !callback.requestedSkillId) return null;
    const summary = skills.find((skill) => (
      skill.skill_id === callback.requestedSkillId
      && skill.connection?.kind === "composio"
    ));
    return summary
      ? { skillId: summary.skill_id, displayName: formatSkillName(summary.skill_id) }
      : null;
  }, [callback?.requestedSkillId, callback?.result, skills]);

  useEffect(() => {
    // Wait for the authenticated catalog before deciding whether the callback
    // candidate is trusted. A missing/edited value therefore stays generic.
    if (!callback?.result || !skillWiring.data || skillWiring.loading) return;
    onCallbackTargetResolved?.(trustedCallbackTarget);
  }, [callback?.result, onCallbackTargetResolved, skillWiring.data, skillWiring.loading, trustedCallbackTarget]);

  return (
    <div>
      {skillWiring.loading && !skillWiring.data ? (
        <div className="flex items-center justify-center py-6" aria-label={t("connections:skills.loading")}>
          <Spinner className="size-5" />
        </div>
      ) : skillWiring.error && !skillWiring.data ? (
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm text-destructive" role="alert">
          <span className="flex items-center gap-2">
            <AlertCircle className="size-4" />
            {t("connections:skills.couldNotLoadStatus")}
          </span>
          <Button variant="outline" size="sm" onClick={() => void skillWiring.refresh()}>
            {t("common:actions.retry")}
          </Button>
        </div>
      ) : skills.length > 0 ? (
        <SkillList
          variant="connections"
          skills={skills.map((summary) => ({
            id: summary.skill_id,
            description: summary.description,
            wiring: summary,
          }))}
          getToken={getToken}
          wiringLoading={skillWiring.loading}
          wiringError={skillWiring.error}
          onRefresh={skillWiring.refresh}
          saveSkillSecret={skillWiring.saveSkillSecret}
          deleteSkillSecret={skillWiring.deleteSkillSecret}
          composioCompatibilityEnabled={compatibility.enabled}
          composioCompatibilityLoading={compatibility.loading}
          composioCompatibilityProbes={compatibility.results}
          openComposioSkillId={trustedCallbackTarget?.skillId ?? openComposioSkillId}
          ariaLabel={t("connections:skills.title")}
        />
      ) : (
        <p className="p-4 text-sm text-muted-foreground">
          {t("connections:skills.noSkills")}
        </p>
      )}
    </div>
  );
}

function LazySkillsSection({
  getToken,
  callback,
  openComposioSkillId = null,
  onCallbackTargetResolved,
}: ConnectionsListProps & {
  callback?: ComposioCallbackLocation | null;
  openComposioSkillId?: string | null;
  onCallbackTargetResolved?: (target: ComposioCallbackTarget | null) => void;
}) {
  const { t } = useTranslation();
  // Loading the descriptor catalog is necessary to verify a callback target,
  // but only a verified target opens a row inside the section.
  const shouldLoadForCallback = Boolean(callback?.result);
  const [open, setOpen] = useState(shouldLoadForCallback);
  const [loaded, setLoaded] = useState(shouldLoadForCallback);

  const onOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setLoaded(true);
  };

  return (
    <section className="bg-card" aria-labelledby="skills-heading">
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <h2 id="skills-heading" className="text-sm font-semibold tracking-tight">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-muted/50">
            <span className="flex items-center gap-2">
              <Key className="size-4 text-blue-600 dark:text-blue-400" />
              {t("connections:skills.title")}
            </span>
            <ChevronDown className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
        </h2>
        <CollapsibleContent className="border-t">
          {loaded && (
            <SkillWiringPanel
              getToken={getToken}
              callback={callback}
              openComposioSkillId={openComposioSkillId}
              onCallbackTargetResolved={onCallbackTargetResolved}
            />
          )}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

function LazyMcpConnectionsSection({ getToken }: ConnectionsListProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const onOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setLoaded(true);
  };

  return (
    <section className="bg-card" aria-labelledby="mcp-heading">
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <h2 id="mcp-heading" className="text-sm font-semibold tracking-tight">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-muted/50">
            <span className="flex items-center gap-2">
              <McpIcon className="size-4" />
              {t("connections:mcpServers.title")}
            </span>
            <ChevronDown className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
        </h2>
        <CollapsibleContent className="border-t">
          {loaded && (
            <div className="divide-y">
              <McpServersPanel getToken={getToken} />
              <McpAgentAccessConnectionProvider getToken={getToken} />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

function LazyApiKeysSection({ getToken }: ConnectionsListProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const onOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setLoaded(true);
  };

  return (
    <section id="api-keys" className="scroll-mt-6 bg-card" aria-labelledby="byok-keys-heading">
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <h2 id="byok-keys-heading" className="text-sm font-semibold tracking-tight">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-muted/50">
            <span className="flex items-center gap-2">
              <KeyRound className="size-4 text-blue-600 dark:text-blue-400" />
              {t("connections:byok.title")}
            </span>
            <ChevronDown className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
        </h2>
        <CollapsibleContent className="border-t">
          {loaded && <ByokKeysPanel getToken={getToken} />}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

function SlackChannelItem({ getToken, me }: ConnectionsListProps & { me: Me | null }) {
  const { t } = useTranslation();
  // getTenantAgentEditorAccess keeps the raw-role fallback for older bridges
  // whose /api/me omits is_platform_admin / is_tenant_admin. The server remains
  // the authority: it scopes every Slack route to the verified JWT tenant.
  const canManage = getTenantAgentEditorAccess(me).canManageTenantAgents;

  return (
    <div>
      <h3 id="slack-item-heading" className="flex items-center gap-2 px-4 pt-4 text-sm font-medium">
        <SlackIcon className="size-4" />
        {t("connections:slack.title")}
      </h3>
      <SlackInstallSection
        key={`${me?.user_id ?? "none"}:${canManage}`}
        getToken={getToken}
        canManage={canManage}
      />
    </div>
  );
}

function LazyChannelsSection({ getToken, me }: ConnectionsListProps & { me: Me | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const onOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setLoaded(true);
  };

  return (
    <section className="bg-card" aria-labelledby="channels-heading">
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <h2 id="channels-heading" className="text-sm font-semibold tracking-tight">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-muted/50">
            <span className="flex items-center gap-2">
              <MessagesSquare className="size-4" />
              {t("connections:channels.title")}
            </span>
            <ChevronDown className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
        </h2>
        <CollapsibleContent className="border-t">
          {loaded && (
            <div className="divide-y">
              <SlackChannelItem getToken={getToken} me={me} />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

export function ConnectionsList({ getToken }: ConnectionsListProps) {
  const { t } = useTranslation();
  const [composioCallback] = useState(composioCallbackFromLocation);
  const [composioCallbackTarget, setComposioCallbackTarget] = useState<ComposioCallbackTarget | null>(null);
  const ms365 = useConnections(getToken);
  const google = useGoogleConnections(getToken);
  const userSecrets = useUserSecrets(getToken);
  const apiKeyProviders = useMemo(
    () => (userSecrets.availableSecrets ?? [])
      .filter((secret) => !isByokManagedSecret(secret))
      .map((secret) => createApiKeyProvider(secret, t)),
    [t, userSecrets.availableSecrets],
  );

  useEffect(() => {
    if (!composioCallback.hasComposioParams || typeof window === "undefined") return;
    const next = new URL(window.location.href);
    next.searchParams.delete("composio");
    next.searchParams.delete("composio_skill");
    window.history.replaceState(
      window.history.state,
      "",
      `${next.pathname}${next.search}${next.hash}`,
    );
  }, [composioCallback.hasComposioParams]);

  const onComposioCallbackTargetResolved = useCallback((target: ComposioCallbackTarget | null) => {
    setComposioCallbackTarget((current) => (
      current?.skillId === target?.skillId && current?.displayName === target?.displayName
        ? current
        : target
    ));
  }, []);

  const callbackSkillName = composioCallbackTarget?.displayName;
  const callbackMessage = composioCallback.result === "connected"
    ? t(
      callbackSkillName
        ? "connections:composio.callbackConnected"
        : "connections:composio.callbackConnectedGeneric",
      callbackSkillName ? { skill: callbackSkillName } : undefined,
    )
    : composioCallback.result === "cancelled"
      ? t(
        callbackSkillName
          ? "connections:composio.callbackCancelled"
          : "connections:composio.callbackCancelledGeneric",
        callbackSkillName ? { skill: callbackSkillName } : undefined,
      )
      : composioCallback.result === "failed"
        ? t(
          callbackSkillName
            ? "connections:composio.callbackFailed"
            : "connections:composio.callbackFailedGeneric",
          callbackSkillName ? { skill: callbackSkillName } : undefined,
        )
        : null;

  return (
    <div className="space-y-3" aria-label={t("connections:list.availableConnections")}>
      {callbackMessage && (
        <div className="flex items-start gap-2 rounded-md border border-muted-foreground/20 bg-muted/40 p-3 text-sm" role="status">
          {composioCallback.result === "connected" ? (
            <Check className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-400" />
          ) : (
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          )}
          <span>{callbackMessage}</span>
        </div>
      )}
      <Card className="divide-y overflow-hidden shadow-none">
        {OAUTH_PROVIDER_REGISTRY.map((provider) => provider.id === "microsoft-365" ? (
          <Microsoft365ConnectionProvider
            key={provider.id}
            provider={provider}
            data={ms365.data}
            loading={ms365.loading}
            error={ms365.error}
            me={ms365.me}
            meLoading={ms365.meLoading}
            refresh={ms365.refresh}
            connect={ms365.connect}
            disconnect={ms365.disconnect}
            generateOnboardingLink={ms365.generateOnboardingLink}
          />
        ) : (
          <GoogleConnectionProvider
            key={provider.id}
            provider={provider}
            data={google.data}
            loading={google.loading}
            error={google.error}
            refresh={google.refresh}
            connect={google.connect}
            updateConnection={google.updateConnection}
            setDefault={google.setDefault}
            disconnect={google.disconnect}
            reconnect={google.reconnect}
          />
        ))}

        {apiKeyProviders.map((provider) => {
          const storedSecret = userSecrets.storedSecrets?.find(
            (secret) => secret.secret_name === secretKey(provider.secret),
          );

          return (
            <ApiKeyConnectionProvider
              key={provider.id}
              provider={provider}
              storedSecret={storedSecret}
              loading={userSecrets.loading}
              error={userSecrets.error}
              refresh={userSecrets.refresh}
              saveSecret={userSecrets.saveSecret}
              deleteSecret={userSecrets.deleteSecret}
            />
          );
        })}

        <LazySkillsSection
          getToken={getToken}
          callback={composioCallback.result ? composioCallback : null}
          openComposioSkillId={composioCallbackTarget?.skillId ?? null}
          onCallbackTargetResolved={onComposioCallbackTargetResolved}
        />

        <LazyMcpConnectionsSection getToken={getToken} />

        <LazyApiKeysSection getToken={getToken} />
        <LazyChannelsSection getToken={getToken} me={ms365.me} />
      </Card>

      {userSecrets.error && !userSecrets.availableSecrets && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          role="alert"
        >
          <span className="flex items-center gap-2">
            <AlertCircle className="size-4" />
            {t("connections:list.apiTokenProvidersLoadError")}
          </span>
          <Button variant="outline" size="sm" onClick={() => void userSecrets.refresh()}>
            {t("common:actions.retry")}
          </Button>
        </div>
      )}

      {userSecrets.availableSecrets?.length === 0 && !userSecrets.error && (
        <p className="text-sm text-muted-foreground">{t("connections:list.noApiTokenServices")}</p>
      )}
    </div>
  );
}
